/**
 * Keeps dashing-ui's component overrides in sync with the current brand/flags.
 * - `VisionPrediction.topPerformingMediaUrl` and the "top performer" message per current brand.
 * - YouTube-compliance channel icon styling when the flag is off.
 */

// Side-effect imports — register each component's `DashingUIOverrideRegistry` augmentation.
import '@dashhudson/dashing-ui/vision-prediction';
import '@dashhudson/dashing-ui/channel-icon';
import '@dashhudson/dashing-ui/channel-icon-button';
import '@dashhudson/dashing-ui/platform-media-chip';
import { setDashingUIOverrides } from '@dashhudson/dashing-ui/plugin';
import { type App, watch } from 'vue';

import { useYouTubeCompliance } from '@/composables/useYouTubeCompliance';
import { useStarImprovementQuery } from '@/shared/queries/star-improvement';
import { useIdentityStore } from '@/stores/identity';
import { formatPercentage } from '@/utils/formatters';

// Below this, the star improvement isn't compelling enough to cite
const STAR_IMPROVEMENT_COPY_THRESHOLD = 0.1;

// Library filtered to top/high predictions, sorted by predicted performance
function topPerformingMediaUrlFor(brandLabel: string): string {
  const params = new URLSearchParams({ performance: 'top,high', sort: '-PERFORMANCE' });
  return `/${brandLabel}/library/discover?${params.toString()}`;
}

export function topPerformerMessageFor(starImprovement: number | null): string {
  if (starImprovement == null || starImprovement < STAR_IMPROVEMENT_COPY_THRESHOLD) {
    return "This content is likely a top performer, based on your content's past performance.";
  }
  return `This content is likely a top performer. Vision predicts ${formatPercentage(starImprovement)} higher performance for your brand.`;
}

export default {
  install(app: App) {
    // runWithContext: useStarImprovementQuery injects app-level query options
    app.runWithContext(() => {
      const identityStore = useIdentityStore();
      const { youTubeMetricsAllowed } = useYouTubeCompliance();
      const { starImprovement } = useStarImprovementQuery();

      watch(
        [() => identityStore.currentBrandLabel, starImprovement],
        ([brandLabel, improvement]) => {
          setDashingUIOverrides('vision-prediction', {
            topPerformingMediaUrl: brandLabel ? topPerformingMediaUrlFor(brandLabel) : undefined,
            message: { top: topPerformerMessageFor(improvement) },
          });
        },
        { immediate: true },
      );

      // When YouTube compliance is off, render channel icons as neutral avatars and
      // the larger black ChannelIconButton treatment, and hide the YouTube Shorts
      // icon on PlatformMediaChip (generic video icon instead), app-wide.
      watch(
        youTubeMetricsAllowed,
        (isCompliant) => {
          setDashingUIOverrides('channel-icon', isCompliant ? null : { variant: 'avatar' });
          setDashingUIOverrides(
            'channel-icon-button',
            isCompliant ? null : { size: 64, fill: 'black' },
          );
          setDashingUIOverrides(
            'platform-media-chip',
            isCompliant ? null : { useVideoIconForYouTube: true },
          );
        },
        { immediate: true },
      );
    });
  },
};
